import React, { useState, useEffect } from 'react';
import { Chip, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

// Adresa publica a listei de instante disponibile pe portalul DEN (den.ro).
// Gazduita pe juristpro.ro (acelasi proprietar) - se poate citi direct din frontend.
const DEN_JSON_URL = 'https://juristpro.ro/next/den.json';

// Etichetele afisate atat pe pagina de dosar electronic cat si in sertarul DEN,
// ca sa fie citite ca o singura functionalitate.
export const DEN_PORTAL_LABEL = 'Dosarul se află pe portalul instanțelor de judecată den.ro';
export const DEN_PORTAL_LABEL_NEGATIV = 'Dosarul nu se află pe portalul instanțelor de judecată den.ro';

// Hook care incarca o singura data lista de instante DEN si o pastreaza.
// In caz de eroare returneaza o lista goala (nu blocheaza pagina).
export function useDenInstante() {
  const [instante, setInstante] = useState([]);

  useEffect(() => {
    let activ = true;
    (async () => {
      try {
        const response = await fetch(DEN_JSON_URL);
        if (!response.ok) return;
        const data = await response.json();
        if (activ && Array.isArray(data)) {
          setInstante(data);
        }
      } catch (error) {
        // esec silentios - pur si simplu nu afisam indicatorul
      }
    })();
    return () => {
      activ = false;
    };
  }, []);

  return instante;
}

// Extrage codul instantei (id_instanta) din numarul unic al dosarului.
// Numarul are forma NNNN/CCC/AAAA (ex. 445/2/2024) - segmentul din mijloc (CCC) este codul instantei.
// Returneaza un intreg sau null daca numarul nu respecta tiparul numar/numar/an.
export function extrageCodInstantaDinNumarDosar(numarDosar) {
  const parti = (numarDosar || '').trim().split('/');
  if (parti.length < 3) return null;
  const numar = parti[0].trim();
  const cod = parti[1].trim();
  const an = parti[2].trim();
  if (!/^\d+$/.test(numar) || !/^\d+$/.test(cod) || !/^\d+$/.test(an)) return null;
  return parseInt(cod, 10);
}

// Verifica statusul unei instante (dupa id_instanta numeric) pe portalul DEN.
// Returneaza:
//   'activ'      - instanta exista si este activa (activ === true)  -> bifa verde
//   'inactiv'    - instanta exista dar este dezactivata (are `err`) -> indicator negativ
//   'negasit'    - id de instanta valid, dar negasit in den.json    -> indicator negativ
//   'necunoscut' - nu avem un id valid de verificat sau lista nu e incarcata -> niciun indicator
export function statusInstantaDen(instante, idInstanta) {
  const id = parseInt(idInstanta, 10);
  if (Number.isNaN(id)) return 'necunoscut';
  if (!Array.isArray(instante) || instante.length === 0) return 'necunoscut';
  const gasita = instante.find((i) => Number(i.id_instanta) === id);
  if (!gasita) return 'negasit';
  return gasita.activ === true ? 'activ' : 'inactiv';
}

// Indicatorul vizual reutilizabil.
// - 'activ'              -> bifa verde + mesaj "Dosarul se află pe portalul..."
// - 'inactiv'/'negasit'  -> indicator rosu/gri + mesaj "Dosarul nu se află pe portalul..."
// - 'necunoscut'         -> nimic (nu avem ce verifica)
export function DenPortalIndicator({ status, size = 'small' }) {
  if (status === 'activ') {
    return (
      <Tooltip title={DEN_PORTAL_LABEL}>
        <Chip
          icon={<CheckCircleIcon sx={{ color: '#2e7d32 !important' }} />}
          label={DEN_PORTAL_LABEL}
          size={size}
          sx={{
            backgroundColor: 'rgba(46, 125, 50, 0.1)',
            color: '#2e7d32',
            fontWeight: 600,
            border: '1px solid rgba(46, 125, 50, 0.35)',
            maxWidth: '100%',
            '& .MuiChip-label': { whiteSpace: 'normal' },
          }}
        />
      </Tooltip>
    );
  }
  if (status === 'inactiv' || status === 'negasit') {
    return (
      <Tooltip title={DEN_PORTAL_LABEL_NEGATIV}>
        <Chip
          icon={<HighlightOffIcon sx={{ color: '#c62828 !important' }} />}
          label={DEN_PORTAL_LABEL_NEGATIV}
          size={size}
          sx={{
            backgroundColor: 'rgba(198, 40, 40, 0.08)',
            color: '#c62828',
            fontWeight: 600,
            border: '1px solid rgba(198, 40, 40, 0.3)',
            maxWidth: '100%',
            '& .MuiChip-label': { whiteSpace: 'normal' },
          }}
        />
      </Tooltip>
    );
  }
  return null;
}
